import { Save, SearchX, UsersRound } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, type Session } from "./api";
import { demoInvestors } from "./demo";
import type { Investor } from "./types";
import {
  BackBar,
  DensityToggle,
  Empty,
  Field,
  fmtInt,
  fmtShortDate,
  Notice,
  PageHead,
  PanelEmpty,
  SearchInput,
  Segmented,
  SkeletonRows,
  StatusDot,
  useIsNarrow,
  useListKeyboard,
  useSearchShortcut,
  type Density
} from "./ui";

const stageOrder = [
  "research",
  "first_contact",
  "meeting_scheduled",
  "diligence",
  "term_sheet",
  "closed"
] as const;

const stageNames: Record<string, string> = {
  research: "Research",
  first_contact: "First contact",
  meeting_scheduled: "Meeting scheduled",
  diligence: "Diligence",
  term_sheet: "Term sheet",
  closed: "Closed"
};

const stageTone: Record<string, "ok" | "warn" | "bad" | "neutral"> = {
  research: "neutral",
  first_contact: "neutral",
  meeting_scheduled: "warn",
  diligence: "warn",
  term_sheet: "ok",
  closed: "ok"
};

export function InvestorsView({
  session,
  demo,
  density,
  onDensityChange
}: {
  session: Session;
  demo: boolean;
  density: Density;
  onDensityChange: (next: Density) => void;
}) {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [stage, setStage] = useState<string>("active");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const searchRef = useRef<HTMLInputElement>(null);
  const narrow = useIsNarrow();
  const canWrite = session.permissions.includes("investors.write");

  useSearchShortcut(searchRef);

  async function load() {
    setLoading(true);
    setError(undefined);
    try {
      const data = demo
        ? { investors: demoInvestors }
        : await api<{ investors: Investor[] }>("/api/investors", session);
      setInvestors(data.investors);
      setSelectedId(
        (current) =>
          current ??
          (window.matchMedia("(max-width: 900px)").matches ? undefined : data.investors[0]?.id)
      );
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "The investor pipeline could not be loaded"
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(
    () =>
      investors.filter(
        (investor) =>
          (stage === "active" ? investor.stage !== "closed" : investor.stage === stage) &&
          `${investor.name} ${investor.category ?? ""}`
            .toLowerCase()
            .includes(search.trim().toLowerCase())
      ),
    [investors, stage, search]
  );

  const selected = investors.find((investor) => investor.id === selectedId);
  const { onKeyDown, rowProps } = useListKeyboard({
    items: filtered,
    selectedId,
    onSelect: setSelectedId
  });

  const stageOptions = [
    {
      value: "active",
      label: "Active",
      count: investors.filter((investor) => investor.stage !== "closed").length
    },
    ...stageOrder.map((key) => ({
      value: key as string,
      label: stageNames[key],
      count: investors.filter((investor) => investor.stage === key).length
    }))
  ];

  return (
    <section
      className="view"
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        if (search) setSearch("");
        else if (selectedId) setSelectedId(undefined);
      }}
    >
      <PageHead
        title="Investors"
        count={loading ? "Loading…" : `${fmtInt(filtered.length)} shown`}
        authority="Admin console"
        authorityMode="own"
      >
        <DensityToggle density={density} onChange={onDensityChange} />
      </PageHead>

      <div className="toolbar">
        <SearchInput
          inputRef={searchRef}
          value={search}
          onChange={setSearch}
          label="Search investors by name or category"
          placeholder="Name or category"
        />
        <Segmented
          label="Pipeline stage"
          value={stage}
          options={stageOptions}
          onChange={setStage}
        />
      </div>

      {error && (
        <Notice
          tone="error"
          className="toolbar-notice"
          action={{ label: "Retry", onClick: () => void load() }}
        >
          {error}
        </Notice>
      )}

      <div className={`split${selectedId ? " has-selection" : ""}`}>
        <div className="split-main">
          <div className="table-wrap" onKeyDown={onKeyDown}>
            <table className="records fixed">
              <colgroup>
                {narrow ? (
                  <>
                    <col style={{ width: "48%" }} />
                    <col style={{ width: "30%" }} />
                    <col style={{ width: "22%" }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: "23%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "24%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} />
                  </>
                )}
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Firm</th>
                  {!narrow && <th scope="col">Category</th>}
                  <th scope="col">Stage</th>
                  {!narrow && <th scope="col">Next action</th>}
                  <th scope="col" className={narrow ? "right" : ""}>
                    Due
                  </th>
                  {!narrow && (
                    <th scope="col" className="right">
                      Ask
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows columns={narrow ? 3 : 6} rows={5} />
                ) : (
                  filtered.map((investor) => (
                    <tr key={investor.id} {...rowProps(investor.id)}>
                      <td className="cell-primary">
                        {narrow ? (
                          <span className="cell-stack">
                            <span className="truncate">{investor.name}</span>
                            <small>{investor.next_action ?? investor.category ?? "—"}</small>
                          </span>
                        ) : (
                          investor.name
                        )}
                      </td>
                      {!narrow && (
                        <td className="cell-muted">{investor.category ?? "Uncategorised"}</td>
                      )}
                      <td>
                        <StatusDot
                          value={stageNames[investor.stage] ?? investor.stage}
                          tone={stageTone[investor.stage]}
                        />
                      </td>
                      {!narrow && <td className="cell-muted">{investor.next_action ?? "—"}</td>}
                      <td className={`cell-faint${narrow ? " right" : ""}`}>
                        {investor.next_action_date ? fmtShortDate(investor.next_action_date) : "—"}
                      </td>
                      {!narrow && (
                        <td className="right num cell-muted">
                          {investor.target_ask ? `$${fmtInt(investor.target_ask)}` : "—"}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {!loading && !filtered.length && (
              <Empty icon={SearchX} title="No investor in this view">
                Change the stage filter or clear the search to see the rest of the pipeline.
              </Empty>
            )}
          </div>
        </div>

        <aside className="panel" aria-label="Selected investor">
          <BackBar label="All investors" onBack={() => setSelectedId(undefined)} />
          {selected ? (
            <InvestorPanel
              key={selected.id}
              investor={selected}
              session={session}
              demo={demo}
              canWrite={canWrite}
              onSaved={(updated) =>
                setInvestors((current) =>
                  current.map((item) => (item.id === updated.id ? updated : item))
                )
              }
            />
          ) : (
            <PanelEmpty icon={UsersRound} title="No investor selected">
              Choose a row to review the relationship, set the next action, and keep notes.
            </PanelEmpty>
          )}
        </aside>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function InvestorPanel({
  investor,
  session,
  demo,
  canWrite,
  onSaved
}: {
  investor: Investor;
  session: Session;
  demo: boolean;
  canWrite: boolean;
  onSaved: (investor: Investor) => void;
}) {
  const [draft, setDraft] = useState(investor);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [failure, setFailure] = useState<string>();

  const dirty = useMemo(
    () =>
      draft.stage !== investor.stage ||
      draft.next_action !== investor.next_action ||
      draft.next_action_date !== investor.next_action_date ||
      draft.notes !== investor.notes ||
      draft.target_ask !== investor.target_ask,
    [draft, investor]
  );

  function update(patch: Partial<Investor>) {
    setDraft((current) => ({ ...current, ...patch }));
    setSaved(false);
    setFailure(undefined);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFailure(undefined);
    try {
      if (!demo)
        await api(`/api/investors/${investor.id}`, session, {
          method: "PATCH",
          body: JSON.stringify({
            stage: draft.stage,
            nextAction: draft.next_action,
            nextActionDate: draft.next_action_date,
            notes: draft.notes,
            targetAsk: draft.target_ask ? Number(draft.target_ask) : null
          })
        });
      onSaved({ ...draft, updated_at: new Date().toISOString() });
      setSaved(true);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : "The investor record could not be saved");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="panel-scroll">
        <header className="panel-head">
          <div>
            <h2>{draft.name}</h2>
            <p>{draft.category ?? "Uncategorised"}</p>
            {investor.legacy_id && (
              <code className="id">Imported from legacy record {investor.legacy_id}</code>
            )}
          </div>
          <StatusDot value={stageNames[draft.stage] ?? draft.stage} tone={stageTone[draft.stage]} />
        </header>

        <form id="investor-form" onSubmit={(event) => void save(event)}>
          <fieldset disabled={!canWrite || busy}>
            <section className="panel-section">
              <h3>Relationship</h3>
              <div className="stack">
                <p className="prose">{investor.background ?? "No background recorded."}</p>
                <div className="row-2">
                  <Field label="Stage" htmlFor="investor-stage">
                    <select
                      id="investor-stage"
                      value={draft.stage}
                      onChange={(event) => update({ stage: event.target.value })}
                    >
                      {stageOrder.map((key) => (
                        <option value={key} key={key}>
                          {stageNames[key]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Target ask" htmlFor="investor-ask" hint="USD">
                    <input
                      id="investor-ask"
                      type="number"
                      min="0"
                      step="1000"
                      inputMode="numeric"
                      value={draft.target_ask ?? ""}
                      onChange={(event) => update({ target_ask: event.target.value || null })}
                      placeholder="Not set"
                    />
                  </Field>
                </div>
              </div>
            </section>

            <section className="panel-section">
              <h3>Next action</h3>
              <div className="stack">
                <Field label="What happens next" htmlFor="investor-action">
                  <input
                    id="investor-action"
                    type="text"
                    value={draft.next_action ?? ""}
                    onChange={(event) => update({ next_action: event.target.value || null })}
                    placeholder="Send the product-data follow-up"
                  />
                </Field>
                <Field label="Due date" htmlFor="investor-date">
                  <input
                    id="investor-date"
                    type="date"
                    value={draft.next_action_date ?? ""}
                    onChange={(event) => update({ next_action_date: event.target.value || null })}
                  />
                </Field>
              </div>
            </section>

            <section className="panel-section">
              <h3>Notes</h3>
              <Field label="Context that should survive the next handoff" htmlFor="investor-notes">
                <textarea
                  id="investor-notes"
                  rows={6}
                  value={draft.notes ?? ""}
                  onChange={(event) => update({ notes: event.target.value || null })}
                  placeholder="Who was in the room, what they asked for, what you promised."
                />
              </Field>
            </section>
          </fieldset>

          {(failure || saved) && (
            <div className="panel-section">
              {failure && <Notice tone="error">{failure}</Notice>}
              {saved && !failure && <Notice tone="success">Investor record saved.</Notice>}
            </div>
          )}
        </form>
      </div>

      {canWrite && (
        <footer className="panel-foot">
          {dirty && (
            <span className="dirty-note">
              <i aria-hidden="true" />
              Unsaved changes
            </span>
          )}
          <span className="spacer" />
          <button
            type="submit"
            form="investor-form"
            className="btn btn-primary"
            disabled={busy || !dirty}
          >
            <Save aria-hidden="true" /> {busy ? "Saving…" : "Save record"}
          </button>
        </footer>
      )}
    </>
  );
}
